import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useHelp } from "@/context/HelpContext";
import { toast } from "sonner";
import { BlogTile } from "@/pages/Blog";
import BookingForm from "@/components/BookingForm";
import QuotaCalendar from "@/components/QuotaCalendar";
import MessagesPanel from "@/components/MessagesPanel";
import MeasurementsPanel from "@/components/MeasurementsPanel";
import RemindersPanel from "@/components/RemindersPanel";
import LargeConsumerModule from "@/components/LargeConsumerModule";
import AddressMap from "@/components/AddressMap";
import ProfilePanel from "@/components/ProfilePanel";
import { Droplets, TrendingUp, Bell, Trash2 } from "lucide-react";

export default function PoolOwnerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { setHelpKey } = useHelp();
  const [muni, setMuni] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [today, setToday] = useState(null);
  const [refills, setRefills] = useState([]);
  const [tab, setTab] = useState("overview");
  const [prefillDate, setPrefillDate] = useState(null);
  const [refillForm, setRefillForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    volume_m3: "",
    note: "",
  });

  useEffect(() => {
    setHelpKey(`owner.${tab}`);
    return () => setHelpKey(null);
  }, [tab, setHelpKey]);

  // Handle rejection-email deep link: /app?prefill_date=YYYY-MM-DD&book=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pd = params.get("prefill_date");
    const wantsBook = params.get("book") === "1";
    if (pd) {
      setPrefillDate(pd);
      toast.info(t("prefill_toast_title"), {
        description: `${t("prefill_toast_desc")} · ${pd}`,
      });
    }
    if (wantsBook) setTab("book");
    if (pd || wantsBook) {
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState(null, "", clean);
    }
  }, []);

  const todayStr = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [m, b, av, rf] = await Promise.all([
      api.get("/municipalities/mine"),
      api.get("/bookings"),
      api.get(`/quota/availability?day=${todayStr}`),
      api.get("/refills"),
    ]);
    setMuni(m.data);
    setBookings(b.data);
    setToday(av.data);
    setRefills(rf.data);
  };

  useEffect(() => {
    load();
  }, []);

  const deleteBooking = async (id) => {
    await api.delete(`/bookings/${id}`);
    load();
  };

  const submitRefill = async (e) => {
    e.preventDefault();
    await api.post("/refills", {
      date: refillForm.date,
      volume_m3: parseFloat(refillForm.volume_m3),
      note: refillForm.note,
    });
    toast.success(t("report_refill") + " ✓");
    setRefillForm({ date: new Date().toISOString().slice(0, 10), volume_m3: "", note: "" });
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-caps">{muni?.name}</div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{t("my_pool")}</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-primary/40">
          <CardContent className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="label-caps">{t("available_today")}</div>
              <Droplets className="h-4 w-4 text-primary" />
            </div>
            <div className="flex items-baseline gap-2" data-testid="owner-kpi-avail">
              <span className="font-mono-jb text-3xl font-semibold text-primary">
                {today?.available_m3 ?? "—"}
              </span>
              <span className="text-sm text-muted-foreground">/ {today?.quota_m3 ?? "—"} m³</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="label-caps">{t("bookings")}</div>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="font-mono-jb text-3xl font-semibold" data-testid="owner-kpi-bookings">
              {bookings.length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="label-caps">{t("refill_report_required")}</div>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="font-mono-jb text-3xl font-semibold">
              {muni?.refill_reporting_required ? "ON" : "OFF"}
            </div>
          </CardContent>
        </Card>
      </div>

      <BlogTile />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="owner-tabs" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">{t("dashboard")}</TabsTrigger>
          <TabsTrigger value="book" data-testid="tab-book">{t("new_booking")}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">{t("calendar")}</TabsTrigger>
          <TabsTrigger value="measurements" data-testid="tab-measurements">{t("measurements")}</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">{t("reminders")}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">{t("messages")}</TabsTrigger>
          <TabsTrigger value="location" data-testid="tab-location">{t("location")}</TabsTrigger>
          {muni?.refill_reporting_required && (
            <TabsTrigger value="refill" data-testid="tab-refill">{t("report_refill")}</TabsTrigger>
          )}
          <TabsTrigger value="large" data-testid="tab-large">{t("large_consumer")}</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfilePanel />
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("bookings")}</h3>
              {bookings.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("no_bookings")}</div>
              ) : (
                <ul className="divide-y divide-border">
                  {bookings.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center gap-3 py-3">
                      <div className="min-w-[110px] font-mono-jb text-sm" title={b.date}>{formatDate(b.date)}</div>
                      <div className="font-mono-jb text-sm">{b.volume_m3} m³</div>
                      <div className="flex-1 truncate text-sm text-muted-foreground">{b.note}</div>
                      <StatusBadge status={b.status} t={t} />
                      {b.status === "pending" && (
                        <Button variant="ghost" size="icon" onClick={() => deleteBooking(b.id)} data-testid={`owner-del-${b.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="book">
          <BookingForm onCreated={load} defaultDate={prefillDate} />
        </TabsContent>

        <TabsContent value="calendar">
          <QuotaCalendar />
        </TabsContent>

        <TabsContent value="measurements">
          <MeasurementsPanel />
        </TabsContent>

        <TabsContent value="reminders">
          <RemindersPanel refillRequired={muni?.refill_reporting_required} />
        </TabsContent>

        <TabsContent value="messages">
          <MessagesPanel />
        </TabsContent>

        <TabsContent value="location">
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("location")}</h3>
              <AddressMap
                address={user.address}
                lat={user.lat}
                lng={user.lng}
                readOnly
                onChange={() => {}}
              />
              <div className="mt-3 text-sm text-muted-foreground">
                {user.address || "—"}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {muni?.refill_reporting_required && (
          <TabsContent value="refill">
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="mb-4 text-lg font-semibold">{t("report_refill")}</h3>
                <form onSubmit={submitRefill} className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label>{t("date")}</Label>
                    <Input type="date" value={refillForm.date} onChange={(e) => setRefillForm({ ...refillForm, date: e.target.value })} required data-testid="refill-date" />
                  </div>
                  <div>
                    <Label>{t("volume_m3")}</Label>
                    <Input type="number" step="0.1" value={refillForm.volume_m3} onChange={(e) => setRefillForm({ ...refillForm, volume_m3: e.target.value })} required data-testid="refill-volume" />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" className="w-full" data-testid="refill-submit">{t("submit")}</Button>
                  </div>
                </form>
                {refills.length > 0 && (
                  <ul className="mt-4 divide-y divide-border">
                    {refills.map((r) => (
                      <li key={r.id} className="flex justify-between py-2 font-mono-jb text-sm">
                        <span title={r.date}>{formatDate(r.date)}</span>
                        <span>{r.volume_m3} m³</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="large">
          <LargeConsumerModule />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: "outline",
    approved: "default",
    rejected: "destructive",
    completed: "secondary",
  };
  return <Badge variant={map[status] || "outline"}>{t(status)}</Badge>;
}
